import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@deepguide-ai/ui/card';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';

export const Route = createFileRoute('/integrations/github-app-callback')({
  component: GitHubAppCallback,
});

function GitHubAppCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const handledCallbackKeyRef = useRef<string | null>(null);
  const urlParams = new URLSearchParams(window.location.search);
  const installationIdParam = urlParams.get('installation_id');
  const setupActionParam = urlParams.get('setup_action');
  const stateParam = urlParams.get('state');

  const handleInstallationCallback = useMutation(trpc.githubApp.handleInstallationCallback.mutationOptions({
    onSuccess: () => {
      setStatus('success');
      toast.success('GitHub App installed successfully', {
        description: 'You can now connect repositories to your workspace.',
      });

      // If this is a popup, send message to parent and close
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'github-app-installed',
            success: true,
            installationId: installationIdParam ? parseInt(installationIdParam) : undefined,
            setupAction: setupActionParam || undefined,
          },
          window.location.origin
        );
        setTimeout(() => {
          window.close();
        }, 2000);
      } else {
        // Otherwise redirect to integrations page
        setTimeout(() => {
          navigate({ to: '/workspace/integrations/github' });
        }, 2000);
      }
    },
    onError: (error) => {
      setStatus('error');
      setErrorMessage(error.message || 'Failed to complete GitHub App installation');
      toast.error('Installation failed', {
        description: error.message,
        variant: 'destructive',
      });

      // Send error message to parent if popup
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'github-app-error',
            error: error.message
          },
          window.location.origin
        );
      }
    },
  }));
  const { mutate: completeInstallation } = handleInstallationCallback;

  useEffect(() => {
    // Parse URL parameters
    const installationId = installationIdParam;
    const setupAction = setupActionParam;
    // Check if we're in development and received params from production redirect
    if (process.env.NODE_ENV === 'development' && window.location.hostname !== 'localhost') {
      // Redirect to localhost with the same params
      const localUrl = `http://localhost:5173/integrations/github-app-callback${window.location.search}`;
      window.location.href = localUrl;
      return;
    }

    if (installationId && stateParam) {
      const callbackKey = `${installationId}:${stateParam}`;
      if (handledCallbackKeyRef.current === callbackKey) return;
      handledCallbackKeyRef.current = callbackKey;

      completeInstallation({
        installationId: parseInt(installationId),
        setupAction: setupAction || undefined,
        state: stateParam,
      });
    } else {
      // No installation ID, this might be an error
      setStatus('error');
      setErrorMessage('This authorization has no valid connection request. Start the connection again from the intended workspace.');

      // Check if user cancelled
      const error = urlParams.get('error');
      if (error === 'access_denied') {
        setErrorMessage('GitHub App installation was cancelled');
      }
    }
  }, [completeInstallation, installationIdParam, setupActionParam, stateParam]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">
            {status === 'processing' && 'Completing GitHub App Installation'}
            {status === 'success' && 'Installation Successful'}
            {status === 'error' && 'Installation Failed'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center space-y-4">
            {status === 'processing' && (
              <>
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
                <p className="text-center text-muted-foreground">
                  Setting up your GitHub App installation...
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle2 className="h-12 w-12 text-green-500" />
                <p className="text-center text-muted-foreground">
                  GitHub App has been successfully installed!
                </p>
                <p className="text-center text-sm text-muted-foreground">
                  {window.opener ? 'This window will close automatically...' : 'Redirecting to integrations...'}
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <XCircle className="h-12 w-12 text-destructive" />
                <p className="text-center text-muted-foreground">
                  {errorMessage}
                </p>
                <p className="text-center text-sm text-muted-foreground">
                  {window.opener ? (
                    <button
                      className="text-primary underline"
                      onClick={() => window.close()}
                    >
                      Close this window
                    </button>
                  ) : (
                    <button
                      className="text-primary underline"
                      onClick={() => navigate({ to: '/workspace/integrations' })}
                    >
                      Return to integrations
                    </button>
                  )}
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
